import { Injectable } from '@angular/core'
import {
  autoQuestionFormDefaults,
  FormStep,
  IAutoQuestionAutomationFormData,
  IAutoQuestionFormData,
} from '@employer/app/components/jobs-automation/automation-auto-question/auto-questions.model'
import { JobsPostAutomationCompanySettingsService } from '@employer/app/modules/customer/views/jobs-post-automation-company-settings/jobs-post-automation-company-settings.service'
import { selectors as sharedSelectors } from '@employer/app/store/selectors'
import { isNotNil, uuid } from '@engineering11/utility'
import { ComponentStore } from '@ngrx/component-store'
import { createEntityAdapter, EntityState } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { combineLatest, Observable } from 'rxjs'
import { filter, map, take } from 'rxjs/operators'
import { AutomatedQuestionTriggerForm } from './partials/automated-question-trigger/automated-question-trigger-form.component'
import { AutomatedQuestionNameForm } from './partials/automated-question-name-form/automated-question-name-form.component'

export interface IAutoQuestionStoreState extends EntityState<IAutoQuestionFormData> {
  currentAutoQuestionFormId: string | undefined
  enabled: boolean
}

const autoQuestionAdapter = createEntityAdapter<IAutoQuestionFormData>({
  selectId: autoQuestion => autoQuestion.id,
})

const selectors = autoQuestionAdapter.getSelectors()

const initialState: IAutoQuestionStoreState = autoQuestionAdapter.getInitialState({
  currentAutoQuestionFormId: undefined,
  enabled: false,
})

export type AutoQuestionCreatePayload = Omit<Partial<IAutoQuestionFormData>, 'id'> & AutomatedQuestionNameForm & AutomatedQuestionTriggerForm

@Injectable({ providedIn: 'root' })
export class AutoQuestionStore extends ComponentStore<IAutoQuestionStoreState> {
  constructor(private store: Store, private jobsPostAutomationCompanyService: JobsPostAutomationCompanySettingsService) {
    super(initialState)
  }

  // SELECTORS
  readonly getState = this.select(s => s)
  readonly formData$: Observable<IAutoQuestionFormData | undefined> = this.select(s =>
    s.currentAutoQuestionFormId ? selectors.selectEntities(s)[s.currentAutoQuestionFormId] : undefined
  )
  readonly allAutoQuestionFormData$ = this.select(s => selectors.selectAll(s))
  readonly autoQuestionsEnabled$ = this.select(s => s.enabled)
  readonly completedSteps$: Observable<Record<FormStep, boolean>> = this.select(checkStepsCompletion)

  // SYNCHRONOUS GETTERS
  /**
   * Point-in-time snapshot of every question set currently held in the store. Callers that need to
   * persist the list must read it through this rather than off a template binding, whose value is
   * only as fresh as the last change detection pass.
   */
  getAllAutoQuestions(): IAutoQuestionFormData[] {
    return this.get(s => selectors.selectAll(s))
  }

  // ACTIONS
  /**
   * Adds a new question set, defaulting any message the form left blank from the company automation
   * config. Reading that config is asynchronous, so this returns an observable that emits the new id
   * only once the entity is actually in the store — callers must wait for it before persisting, or
   * they will save a list that does not yet contain the new set (CNCT-6792).
   */
  addAutoQuestionFromForm(formData: IAutoQuestionFormData): Observable<string> {
    return combineLatest([
      this.store.select(sharedSelectors.getCurrentUserId).pipe(filter(isNotNil)),
      this.jobsPostAutomationCompanyService.getCurrentCustomerAutomationConfig(),
    ]).pipe(
      // The config is a live document stream; without this, every later emission would add a duplicate set.
      take(1),
      map(([ownerId, companyAutomationConfig]) => {
        const id = uuid()
        const payload: IAutoQuestionFormData = {
          ...autoQuestionFormDefaults,
          ...formData,
          id,
          ownerId,
          initialMessage: formData.initialMessage
            ? formData.initialMessage
            : {
                ...(companyAutomationConfig?.autoQuestions.defaultMessages?.initialMessage ?? autoQuestionFormDefaults.initialMessage),
                enabled: false,
              },
          followUp: formData.followUp
            ? formData.followUp
            : {
                ...(companyAutomationConfig?.autoQuestions.defaultMessages?.followUp ?? autoQuestionFormDefaults.followUp),
                enabled: false,
              },
          warning: formData.warning
            ? formData.warning
            : {
                ...(companyAutomationConfig?.autoQuestions.defaultMessages?.warning ?? autoQuestionFormDefaults.warning),
                enabled: false,
              },
        }
        this.addAutoQuestion(payload)
        this.setCurrentAutoQuestionFormId(id)
        return id
      })
    )
  }

  // EFFECTS
  onUpdateCurrentAutoQuestion = this.effect((formData$: Observable<Partial<IAutoQuestionFormData>>) =>
    formData$.pipe(
      map(formData => {
        const currentAutoQuestionFormId = this.get().currentAutoQuestionFormId
        if (!currentAutoQuestionFormId) return []
        return this.updateAutoQuestion({ ...formData, id: currentAutoQuestionFormId })
      })
    )
  )

  onDeleteAutoQuestion = this.effect((id$: Observable<string>) =>
    id$.pipe(
      map(id => {
        this.removeAutoQuestion(id)
        this.setCurrentAutoQuestionFormId(undefined)
      })
    )
  )

  // UPDATERS

  public readonly toggleAutoQuestionsEnabled = this.updater((s, enabled: boolean) => ({ ...s, enabled }))
  public readonly setCurrentAutoQuestionFormId = this.updater((s, currentAutoQuestionFormId: string | undefined) => ({
    ...s,
    currentAutoQuestionFormId,
  }))

  public readonly setAutoQuestions = this.updater((s, formData: IAutoQuestionAutomationFormData) =>
    autoQuestionAdapter.setAll(formData.autoQuestions, {
      ...s,
      enabled: formData.enabled,
    })
  )

  public readonly resetAutoQuestions = this.updater(s =>
    autoQuestionAdapter.removeAll({
      ...s,
      enabled: false,
    })
  )

  public readonly updateAutoQuestion = this.updater(
    (
      s,
      formData: Partial<IAutoQuestionFormData> & {
        id: string
      }
    ) => autoQuestionAdapter.updateOne({ id: formData.id, changes: formData }, s)
  )
  private readonly addAutoQuestion = this.updater((s, formData: IAutoQuestionFormData) => autoQuestionAdapter.addOne(formData, s))

  private readonly removeAutoQuestion = this.updater((s, id: string) => autoQuestionAdapter.removeOne(id, s))
}

function checkStepsCompletion(s: IAutoQuestionStoreState): Record<FormStep, boolean> {
  const currentAutomation = s.currentAutoQuestionFormId ? selectors.selectEntities(s)[s.currentAutoQuestionFormId] : undefined
  if (!currentAutomation) {
    return {
      Trigger: false,
      Question: false,
      Messages: false,
    }
  }

  return {
    Trigger: !!(currentAutomation.name.length && currentAutomation.swimlane),
    Question: !!currentAutomation.questions.length,
    Messages: currentAutomation.initialMessage.enabled || currentAutomation.followUp.enabled || currentAutomation.warning.enabled,
  }
}
